import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FinetuneDataset } from "../../model_gen/finetuning";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { MoreHorizontalIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import { usePermissions } from "../../PermissionsProvider";
import { useTagConfig } from "../../hooks/useTagConfig";

import moment from "moment";
import PaginatedTable, { PaginatedTableColumn } from "../ui/paginated-table";

interface DatasetsTableProps {
  datasets: FinetuneDataset[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onDeleteDataset?: (dataset: FinetuneDataset) => void;
  emptyTitle?: string;
  emptyDescription?: string;
}

const DatasetsTable: React.FC<DatasetsTableProps> = ({
  datasets,
  loading,
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onDeleteDataset,
  emptyTitle = "No datasets found",
  emptyDescription = "Create a new dataset to get started with model fine-tuning.",
}) => {
  const navigate = useNavigate();
  const { apiKey } = usePermissions();
  const { getTagDisplayConfig } = useTagConfig(apiKey);
  const [deletingDatasetId, setDeletingDatasetId] = useState<number | null>(
    null
  );

  const formatDate = (timestamp: number) => {
    if (timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    if (timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const columns: PaginatedTableColumn<FinetuneDataset>[] = [
    {
      key: "name",
      header: "Name",
      width: "w-[18%]",
      sortable: false,
      render: (dataset) => {
        const name = dataset.name || "";
        const truncated = name.length > 25 ? name.substring(0, 25) + "..." : name;
        const needsTooltip = name.length > 25;
        
        return (
          <div className="relative group">
            <div className="text-sm font-gc-reg text-foreground hover:text-maitai-green transition-colors">
              {truncated}
            </div>
            {needsTooltip && (
              <div className="absolute left-0 bottom-full mb-2 z-50 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                <div className="bg-popover border border-border rounded-md shadow-lg px-2 py-1 max-w-xs">
                  <span className="text-sm text-foreground whitespace-normal break-words">{name}</span>
                </div>
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "meta" as keyof FinetuneDataset,
      header: "Tags",
      width: "w-[15%]",
      sortable: false,
      render: (dataset) => {
        const tags: string[] = (dataset as any)?.meta?.tags || [];
        const hasTestRequests = (dataset as any)?.meta?.used_in_test === true || 
                                (dataset as any)?.meta?.contains_test_requests === true;
        
        // Build all tags including test requests indicator
        const allTags: { key: string; label: string; className: string }[] = [];
        
        if (hasTestRequests) {
          allTags.push({
            key: "_test_requests",
            label: "Test Requests",
            className: "border-violet-500/30 text-violet-700 bg-violet-50 dark:bg-violet-950 dark:text-violet-300",
          });
        }
        
        tags.forEach((tag: string) => {
          const config = getTagDisplayConfig(tag);
          allTags.push({
            key: tag,
            label: config.label,
            className: config.className,
          });
        });
        
        if (allTags.length === 0) {
          return <span className="text-muted-foreground text-xs">-</span>;
        }
        
        // Show up to 2 tags inline
        const visibleTags = allTags.slice(0, 2);
        const hiddenTags = allTags.slice(2);
        const hiddenCount = hiddenTags.length;
        
        return (
          <div className="flex items-center gap-1 flex-wrap">
            {visibleTags.map((tag) => (
              <Badge
                key={tag.key}
                variant="outline"
                className={cn("text-xs whitespace-nowrap", tag.className)}
              >
                {tag.label}
              </Badge>
            ))}
            {hiddenCount > 0 && (
              <div className="relative group/more">
                <span className="text-xs text-muted-foreground hover:text-foreground transition-colors cursor-help">
                  +{hiddenCount}
                </span>
                <div className="absolute left-0 bottom-full mb-2 z-50 invisible group-hover/more:visible opacity-0 group-hover/more:opacity-100 transition-opacity duration-150">
                  <div className="bg-popover border border-border rounded-md shadow-lg p-2 flex flex-col gap-1.5">
                    {hiddenTags.map((tag) => (
                      <Badge
                        key={tag.key}
                        variant="outline"
                        className={cn("text-xs whitespace-nowrap", tag.className)}
                      >
                        {tag.label}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "major_version",
      header: "Version",
      width: "w-[8%]",
      sortable: false,
      render: (dataset) => (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
          v{dataset.major_version || 1}.{dataset.minor_version || 0}
        </span>
      ),
    },
    {
      key: "date_created",
      header: "Date Created",
      width: "w-[15%]",
      sortable: false,
      render: (dataset) =>
        dataset.date_created ? (
          <div className="space-y-1">
            <div className="text-sm font-gc-reg text-foreground">
              {formatDate(dataset.date_created)}
            </div>
            <div className="text-xs text-muted-foreground">
              {formatTime(dataset.date_created)}
            </div>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">-</span>
        ),
    },
    {
      key: "intent_group_name",
      header: "Source",
      width: "w-[20%]",
      sortable: false,
      render: (dataset) => (
        <div className="text-sm font-gc-reg text-foreground">
          {dataset.intent_group_name ||
            (dataset.application_name && dataset.intent_name
              ? `${dataset.application_name} / ${dataset.intent_name}`
              : "-")}
        </div>
      ),
    },
    {
      key: "request_count",
      header: "Requests",
      width: "w-[10%]",
      sortable: false,
      render: (dataset) => (
        <span className="text-sm font-gc-med text-foreground">
          {dataset.request_count ? dataset.request_count.toLocaleString() : "0"}
        </span>
      ),
    },
    {
      key: "id",
      header: "",
      width: "w-[10%]",
      sortable: false,
      render: (dataset) => (
        <div className="relative">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              // Simple dropdown menu using native browser context menu
              const menu = document.createElement("div");
              menu.className =
                "absolute right-0 top-8 z-50 min-w-48 bg-popover border border-border rounded-md shadow-lg py-1";

              // Create delete button
              const deleteButton = document.createElement("button");
              const isDeleting = deletingDatasetId === dataset.id;
              deleteButton.className = `flex items-center w-full px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors ${
                isDeleting
                  ? "text-muted-foreground cursor-not-allowed"
                  : "text-red-600"
              }`;
              deleteButton.disabled = isDeleting;
              deleteButton.innerHTML = isDeleting
                ? `
                <svg class="w-4 h-4 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                </svg>
                Deleting...
              `
                : `
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                </svg>
                Delete
              `;
              deleteButton.onclick = (evt) => {
                evt.stopPropagation();

                // Don't proceed if already deleting
                if (isDeleting) {
                  return;
                }

                menu.remove();

                // Set loading state and call onDeleteDataset
                if (onDeleteDataset) {
                  setDeletingDatasetId(dataset.id);
                  onDeleteDataset(dataset);
                  // Note: The parent component will handle success/error,
                  // but we'll clear loading state after a delay to show feedback
                  setTimeout(() => setDeletingDatasetId(null), 1000);
                }
              };
              menu.appendChild(deleteButton);

              // Position and show menu
              const container = e.currentTarget.parentElement;
              if (container) {
                container.appendChild(menu);

                // Remove menu when clicking outside
                const handleClickOutside = (evt: Event) => {
                  if (!menu.contains(evt.target as Node)) {
                    menu.remove();
                    document.removeEventListener("click", handleClickOutside);
                  }
                };
                setTimeout(
                  () => document.addEventListener("click", handleClickOutside),
                  0
                );
              }
            }}>
            <MoreHorizontalIcon className="w-4 h-4" />
          </Button>
        </div>
      ),
    },
  ];

  const handleRowClick = (dataset: FinetuneDataset) => {
    navigate(`/forge/datasets/${dataset.id}`);
  };

  return (
    <PaginatedTable
      data={datasets}
      columns={columns}
      loading={loading}
      currentPage={currentPage}
      onPageChange={onPageChange}
      pageSize={itemsPerPage}
      totalItems={totalItems}
      serverSidePagination={true}
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
      onRowClick={handleRowClick}
      rowClassName="hover:bg-accent/20 transition-colors"
      showPagination={true}
    />
  );
};

export default DatasetsTable;
